import { useFieldContext } from '@pkg/form/createAppFormHook'
import { useStore } from '@tanstack/react-form'
import type React from 'react'
import { memo, useEffect } from 'react'
import { twMerge } from 'tailwind-merge'

export type EditableShellProps = {
  mode: 'read' | 'edit'
  enterEdit: () => void
  exitRead: () => void
  className?: string
  children: React.ReactNode
}

// Minimal shell wrapper:
// - Adds a `group` so nested elements (e.g., label icons) can respond to hover
// - When in read mode, clicking anywhere inside triggers edit mode
// - Applies appropriate cursor styles using Tailwind
const EditableShellCore = ({ mode, enterEdit, exitRead, className, children }: EditableShellProps) => {
  const fieldCtx = useFieldContext<unknown>()
  const isSubmitSuccessful = useStore(fieldCtx.form.store, s => s.isSubmitSuccessful)
  useEffect(() => {
    if (isSubmitSuccessful) {
      exitRead?.()
    }
  }, [isSubmitSuccessful, exitRead])

  const handleClick = () => {
    if (mode === 'read') enterEdit()
  }

  if (mode === 'read') {
    return (
      <button
        type="button"
        className={twMerge(
          'group relative block w-full cursor-pointer! appearance-none border-0 bg-transparent p-0 text-left font-inherit text-inherit',
          className,
        )}
        onClick={handleClick}
      >
        {children}
      </button>
    )
  }

  return <div className={twMerge('group relative cursor-auto', className)}>{children}</div>
}

export const EditableShell = memo(EditableShellCore)
